// frontend/src/lib/analyzeToolState.ts
// Pure per-tool status computation for the Analyze surfaces (rail nest,
// center Overview, page panels). Extracted from analyze.tsx so all three
// consumers share one source of truth (Piece C). No JSX, no hooks here —
// see hooks/useAnalyzeToolState.ts for the data-gathering layer.

export type ToolId =
  | "field-debate"
  | "chat"
  | "comparison"
  | "coverage-audit"
  | "landscape"
  | "critique";

export type ToolStatus = "ready" | "locked" | "done" | "stale" | "running" | "refreshing";

export interface ToolStatusResult {
  status: ToolStatus;
  /** Present iff status === "locked". */
  lockedReason?: string;
}

export interface ToolStatusInputs {
  /** Papers in the filtered display scope (drives comparison/audit locks). */
  scopedPaperCount: number;
  scoredCount: number;
  isPaid: boolean;
  hasEvidenceOnClaims: boolean;
  chatIndexed: boolean;
  hasDebate: boolean;
  debateStale: boolean;
  debateRunning: boolean;
  /** A batch REFRESH is pending for the cached map (in-memory flag or
   *  server-reported pending_build). Beats stale: the user already acted. */
  debateRefreshing: boolean;
  hasAudit: boolean;
  auditStale: boolean;
  auditRunning: boolean;
  hasComparison: boolean;
  comparisonRunning: boolean;
  hasLandscape: boolean;
  landscapeStale: boolean;
  hasPosition: boolean;
  positionStale: boolean;
  positionRunning: boolean;
  claimCount: number;
  hasArgument: boolean;
  argumentStale: boolean;
  argumentRunning: boolean;
}

/**
 * A full Field Debate build is still assigning papers: the hybrid head landed
 * (partial skeleton map) and the batch tail is in flight, or the server
 * reports a pending full build. A failed build (partial_error) is NOT in
 * flight — the view offers Retry instead.
 */
export function isBuildInFlight(
  d:
    | {
        partial?: boolean;
        partial_error?: string | null;
        pending_build?: { kind: string } | null;
      }
    | null
    | undefined,
): boolean {
  if (!d) return false;
  if (d.partial && !d.partial_error) return true;
  return d.pending_build?.kind === "build";
}

/**
 * Whether a debate map counts as "exists" for downstream readiness (Landscape
 * color-by-camp lock, Position-analysis lock, FD done/stale chips). A
 * `partial: true` skeleton map (FD Stage 2 — hybrid head assigned, batch
 * pass pending) has head-only camp paper_ids, so tools built on it would
 * present incomplete analytics as complete. FieldDebateView renders partial
 * maps directly from the query data — this gate is readiness-only.
 */
export function isDebateMapComplete(
  d: { partial?: boolean } | null | undefined,
): boolean {
  return !!d && !d.partial;
}

/**
 * Status precedence (preserved exactly from the pre-Piece-C analyze.tsx):
 * running > cached(stale|done) > locked > ready. Cached data beats the lock
 * gate so a small Working-Set scope never hides a perfectly viewable result.
 */
export function computeToolStatuses(
  i: ToolStatusInputs,
): Record<ToolId, ToolStatusResult> {
  const debate: ToolStatusResult = i.debateRunning
    ? { status: "running" }
    : i.hasDebate
      ? i.debateRefreshing
        ? { status: "refreshing" }
        : { status: i.debateStale ? "stale" : "done" }
      : i.scoredCount < 15
        ? { status: "locked", lockedReason: `Need 15+ scored (have ${i.scoredCount})` }
        : { status: "ready" };

  const audit: ToolStatusResult = i.auditRunning
    ? { status: "running" }
    : i.scopedPaperCount >= 10
      ? i.hasAudit
        ? { status: i.auditStale ? "stale" : "done" }
        : { status: "ready" }
      : { status: "locked", lockedReason: `Need 10+ papers (have ${i.scopedPaperCount})` };

  const comparison: ToolStatusResult = i.comparisonRunning
    ? { status: "running" }
    : i.scopedPaperCount < 2
      ? { status: "locked", lockedReason: "Need 2+ papers in scope" }
      : i.hasComparison
        ? { status: "done" }
        : { status: "ready" };

  // Chat is always clickable — the panel handles the not-indexed empty
  // state with an inline Build CTA. chatIndexed only flavors descriptions.
  const chat: ToolStatusResult = { status: "ready" };

  const landscape: ToolStatusResult = !i.hasDebate
    ? { status: "locked", lockedReason: "Run Field Debate first" }
    : i.hasLandscape
      ? { status: i.landscapeStale ? "stale" : "done" }
      : { status: "ready" };

  return {
    "field-debate": debate,
    chat,
    comparison,
    "coverage-audit": audit,
    landscape,
    critique: computeCritiqueStatuses(i).aggregate,
  };
}

export interface CritiqueStatuses {
  soundness: ToolStatusResult;
  position: ToolStatusResult;
  /** Worst-of over unlocked halves: running > stale > ready > done.
      Both halves locked → locked (soundness's reason wins — it unlocks first). */
  aggregate: ToolStatusResult;
}

export function computeCritiqueStatuses(i: ToolStatusInputs): CritiqueStatuses {
  // Same precedence as every other tool: running > cached(stale|done) >
  // locked > ready — cached results stay viewable behind a lock gate.
  const soundness: ToolStatusResult = i.argumentRunning
    ? { status: "running" }
    : i.hasArgument
      ? { status: i.argumentStale ? "stale" : "done" }
      : !i.isPaid
        ? { status: "locked", lockedReason: "Paid plans only" }
        : i.claimCount === 0
          ? { status: "locked", lockedReason: "Add claims to your map first" }
          : { status: "ready" };

  const position: ToolStatusResult = i.positionRunning
    ? { status: "running" }
    : i.hasPosition
      ? { status: i.positionStale ? "stale" : "done" }
      : !i.isPaid
        ? { status: "locked", lockedReason: "Paid plans only" }
        : !i.hasDebate
          ? { status: "locked", lockedReason: "Run Field Debate first" }
          : !i.hasEvidenceOnClaims
            ? { status: "locked", lockedReason: "Add evidence to your claims" }
            : { status: "ready" };

  const halves = [soundness, position].filter((h) => h.status !== "locked");
  const aggregate: ToolStatusResult =
    halves.length === 0
      ? { status: "locked", lockedReason: soundness.lockedReason }
      : halves.some((h) => h.status === "running")
        ? { status: "running" }
        : halves.some((h) => h.status === "stale")
          ? { status: "stale" }
          : halves.some((h) => h.status === "ready")
            ? { status: "ready" }
            : { status: "done" };

  return { soundness, position, aggregate };
}

/**
 * Field-Debate `apply_scope` derivation (spec 2026-07-07, review F1-FE). A
 * working set OR an explicit camp/bridges/tension paper-id scope is the
 * user deliberately narrowing the corpus — that intent is sacrosanct, so
 * the backend must NOT also apply its own score/budget scoping on top
 * (apply_scope: false). Default corpus views — even with filter chips or
 * thumbs-downed papers shrinking the display set — DO get scoped
 * (apply_scope: true). Extracted so all three Field-Debate generate call
 * sites in analyze.tsx (initial run, stale-banner rerun, header
 * Regenerate) derive it identically instead of repeating the condition.
 */
export function computeApplyFieldDebateScope(
  wsId: string | undefined,
  paperIdScope: string[] | undefined,
): boolean {
  return !wsId && !paperIdScope?.length;
}
